import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type Region, getDefaultRegion, getRegionByCode, getRegions } from '@luxuryescapes/lib-regions';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { useCreateCreditSecurePaymentLink } from '~/hooks/apis/payments/useCreateCreditSecurePaymentLink';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { CreateCreditSecurePaymentLinkPayload } from '~/services/PaymentsService';
import { mapCountryCodeToRegion } from '~/services/UserHelper';
import { json_headers, request } from '~/services/common';

import ApiError from '~/types/ApiError';

import { isAdmin } from '~/utils/adminPermission';
import getErrorMessage from '~/utils/getErrorMessage';

type DeliveryMode = 'link' | 'sms';

type FormValues = {
  deliveryMode: DeliveryMode;
  amount: string;
  region: string;
  merchantFeesEnabled: boolean;
};

const MIN_CREDIT_AMOUNT = 1;
const DEFAULT_MAX_AUD_CREDIT_AMOUNT = 30000;
const ADMIN_MAX_AUD_CREDIT_AMOUNT = 50000;
const CREDIT_AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const FALLBACK_REGION = {
  code: 'AU',
  name: 'Australia',
  currencyCode: 'AUD',
} as Region;

const defaultFormValues = (regionCode = FALLBACK_REGION.code): FormValues => ({
  deliveryMode: 'link',
  amount: '',
  region: regionCode,
  merchantFeesEnabled: true,
});

export function parseCreditSecurePaymentAmount(
  amount: string,
  minAmount: number = MIN_CREDIT_AMOUNT,
  maxAmount: number = DEFAULT_MAX_AUD_CREDIT_AMOUNT,
): number | null {
  const normalizedAmount = amount.trim();

  if (!CREDIT_AMOUNT_REGEX.test(normalizedAmount)) {
    return null;
  }

  const value = Number(normalizedAmount);

  if (!Number.isFinite(value) || value < minAmount || value > maxAmount) {
    return null;
  }

  return value;
}

/** Build E.164-style international mobile from stored prefix + local number (no spaces). */
function buildCustomerInternationalMobile(phonePrefix: unknown, phone: unknown): string | null {
  const local = phone == null ? '' : String(phone).replace(/\s+/g, '');
  const prefix = phonePrefix == null ? '' : String(phonePrefix).replace(/^\+/, '');
  if (!local || !prefix) {
    return null;
  }
  return `+${prefix}${local}`;
}

function isAustralianSmsMobile(normalized: string) {
  return normalized.startsWith('+614') || normalized.startsWith('+6104');
}

export type CreateCreditSecurePaymentDialogProps = {
  open: boolean;
  onClose: () => void;
  user: App.User;
  tenant: App.Tenant;
};

export default function CreateCreditSecurePaymentDialog({
  open,
  onClose,
  user,
  tenant,
}: CreateCreditSecurePaymentDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useCreateCreditSecurePaymentLink();
  const { user: currentUser } = useCurrentUser();
  const maxAudCreditAmount = useMemo(
    () => (isAdmin(currentUser) ? ADMIN_MAX_AUD_CREDIT_AMOUNT : DEFAULT_MAX_AUD_CREDIT_AMOUNT),
    [currentUser],
  );
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [maxCreditAmount, setMaxCreditAmount] = useState(maxAudCreditAmount);
  const openRef = useRef(open);
  const activeRequestIdRef = useRef(0);

  const canUseSmsBrand = tenant.brand === LUXURY_ESCAPES;

  const smsGreetingName = user.givenName?.trim() ?? '';

  const customerSmsMobile = useMemo(
    () => buildCustomerInternationalMobile(user.phone_prefix, user.phone),
    [user.phone_prefix, user.phone],
  );

  const canOfferSms = Boolean(canUseSmsBrand && customerSmsMobile && isAustralianSmsMobile(customerSmsMobile));

  const tenantRegions = useMemo(() => getRegions(tenant.brand), [tenant.brand]);

  const defaultRegion = useMemo(() => {
    const userRegionCode = user.country_code ? mapCountryCodeToRegion(user.country_code)?.code : undefined;
    if (userRegionCode) {
      const userRegion = getRegionByCode(userRegionCode, tenant.brand);
      if (userRegion) {
        return userRegion;
      }
    }

    return getDefaultRegion(tenant.brand) ?? tenantRegions[0] ?? FALLBACK_REGION;
  }, [tenant.brand, tenantRegions, user.country_code]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: defaultFormValues(defaultRegion.code),
    mode: 'onSubmit',
  });

  const deliveryMode = watch('deliveryMode');
  const selectedRegionCode = watch('region');
  const selectedRegion = useMemo(
    () => getRegionByCode(selectedRegionCode, tenant.brand) ?? defaultRegion,
    [defaultRegion, selectedRegionCode, tenant.brand],
  );
  const selectedCurrency = selectedRegion.currencyCode;

  useEffect(() => {
    openRef.current = open;
    if (!open) {
      activeRequestIdRef.current += 1;
      reset(defaultFormValues(defaultRegion.code));
      setPaymentLink(null);
      setMaxCreditAmount(maxAudCreditAmount);
    }
  }, [defaultRegion.code, maxAudCreditAmount, open, reset]);

  useEffect(() => {
    let stale = false;

    if (selectedCurrency === 'AUD') {
      setMaxCreditAmount(maxAudCreditAmount);
      return;
    }

    request(`${window.configs.API_HOST}/api/fx/v2/convert-currency`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({
        total: maxAudCreditAmount,
        baseCurrencyCode: 'AUD',
        targetCurrencyCode: selectedCurrency,
      }),
    })
      .then(({ result }) => {
        if (stale) {
          return;
        }

        const convertedTotal = Number(result?.convertedTotal);
        setMaxCreditAmount(Number.isFinite(convertedTotal) ? Math.floor(convertedTotal) : maxAudCreditAmount);
      })
      .catch(() => {
        if (!stale) {
          setMaxCreditAmount(maxAudCreditAmount);
        }
      });

    return () => {
      stale = true;
    };
  }, [maxAudCreditAmount, selectedCurrency]);

  useEffect(() => {
    if (!canOfferSms) {
      setValue('deliveryMode', 'link');
    }
  }, [canOfferSms, setValue]);

  const handleDialogClose = () => {
    onClose();
  };

  const handleCopyLink = async () => {
    if (!paymentLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(paymentLink);
      enqueueSnackbar('Payment link copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Unable to copy to clipboard', { variant: 'error' });
    }
  };

  const onSubmit = async (values: FormValues) => {
    const amountNum = parseCreditSecurePaymentAmount(values.amount, MIN_CREDIT_AMOUNT, maxCreditAmount);
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    if (amountNum === null) {
      return;
    }

    if (values.deliveryMode === 'sms') {
      if (!canOfferSms || !customerSmsMobile) {
        enqueueSnackbar("SMS is not available for this customer's phone on file.", { variant: 'error' });
        return;
      }
    }

    const payload: CreateCreditSecurePaymentLinkPayload = {
      amount: amountNum,
      currency: selectedCurrency,
      customerId: user.id_member,
      brand: tenant.brand,
      region: selectedRegion.code,
      ...(!values.merchantFeesEnabled ? { disableCreditPurchaseMerchantFees: true } : {}),
    };

    if (values.deliveryMode === 'sms' && customerSmsMobile) {
      payload.sendToMobileNumber = customerSmsMobile;
      if (smsGreetingName) {
        payload.smsMessageTravellerName = smsGreetingName;
      }
    }

    try {
      const response = await mutation.mutateAsync(payload);
      if (!openRef.current || activeRequestIdRef.current !== requestId) {
        return;
      }
      setPaymentLink(response.result.paymentLink);
      enqueueSnackbar('Secure credit payment link created', { variant: 'success' });
    } catch (err) {
      if (!openRef.current || activeRequestIdRef.current !== requestId) {
        return;
      }
      const message = err instanceof ApiError ? err.message : getErrorMessage(err, 'Unable to create payment link.');
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>Secure credit payment</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {paymentLink && (
              <Alert severity="success">
                <Typography variant="body2" gutterBottom>
                  Payment link generated. Share it securely with the customer.
                </Typography>
                <Box
                  sx={{
                    wordBreak: 'break-all',
                    typography: 'body2',
                    my: 1,
                  }}
                  component="span"
                  display="block"
                >
                  {paymentLink}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<ContentCopyOutlinedIcon />}
                    type="button"
                    onClick={handleCopyLink}
                  >
                    Copy link
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<OpenInNewOutlinedIcon />}
                    type="button"
                    href={paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                  >
                    Open in new tab
                  </Button>
                </Stack>
              </Alert>
            )}

            <FormControl>
              <FormLabel id="delivery-mode-label">Delivery</FormLabel>
              <Controller
                name="deliveryMode"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioGroup {...field} aria-labelledby="delivery-mode-label">
                    <FormControlLabel value="link" control={<Radio />} label="Generate link only" />
                    {canOfferSms && <FormControlLabel value="sms" control={<Radio />} label="Send via SMS" />}
                  </RadioGroup>
                )}
              />
              {!canUseSmsBrand && (
                <FormHelperText>SMS is only available on the Luxury Escapes admin portal.</FormHelperText>
              )}
              {canUseSmsBrand && !canOfferSms && (
                <FormHelperText>
                  SMS is only offered when the customer has an Australian mobile number on file (+614 or +6104).
                </FormHelperText>
              )}
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Amount is required',
                  validate: (value) => {
                    return (
                      parseCreditSecurePaymentAmount(value, MIN_CREDIT_AMOUNT, maxCreditAmount) !== null ||
                      'Enter a valid credit amount'
                    );
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Amount"
                    type="number"
                    required
                    fullWidth
                    inputProps={{ min: MIN_CREDIT_AMOUNT, max: maxCreditAmount, step: 0.01 }}
                    helperText={
                      errors.amount?.message ??
                      `Enter a credit amount between ${MIN_CREDIT_AMOUNT} and ${maxCreditAmount} ${selectedCurrency}`
                    }
                    error={Boolean(errors.amount)}
                  />
                )}
              />
              <Controller
                name="region"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField {...field} label="Region / Currency" select required fullWidth>
                    {tenantRegions.map((region) => (
                      <MenuItem key={region.code} value={region.code}>
                        {`${region.name} (${region.code}) - ${region.currencyCode}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>

            <FormControl>
              <Controller
                name="merchantFeesEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        inputRef={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    }
                    label="Apply merchant fees"
                  />
                )}
              />
            </FormControl>

            {deliveryMode === 'sms' && canOfferSms && (
              <Alert severity="info">
                <Typography variant="body2" component="span" display="block">
                  SMS will be sent to <strong>{customerSmsMobile}</strong> (from customer profile).
                </Typography>
                {smsGreetingName && (
                  <Typography variant="body2" component="span" display="block" sx={{ mt: 1 }}>
                    Greeting name: <strong>{smsGreetingName}</strong> (from customer profile, max 15 characters).
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-start' }}>
          <Button variant="contained" color="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : paymentLink ? 'Create another link' : 'Create link'}
          </Button>
          <Button variant="text" color="primary" type="button" onClick={handleDialogClose}>
            Close
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
